/**
 * Tiny URLSearchParams wrapper used by the typed endpoint registry.
 *
 * Skips entries whose value is `undefined`, returns an empty string when no
 * params remain, and otherwise prefixes the encoded query with `?`. This
 * keeps URL builders in `endpoints.ts` short and avoids hand-built
 * `?foo=&bar=` strings throughout feature `api.ts` files.
 *
 * Empty strings ARE preserved (`{ q: '' }` => `?q=`) so callers retain
 * control over "send the param explicitly empty" vs "omit it" semantics.
 */
export type QueryValue = string | number | boolean | undefined;

/**
 * Object passed to {@link qs}. Each value may be a string, number, boolean,
 * or undefined; undefined keys are skipped. Typed param interfaces in
 * `endpoints.ts` are structurally compatible with this shape.
 */
export type QueryParams = Readonly<Record<string, QueryValue>>;

/**
 * The accepted input is any non-null object — `qs` reads it via
 * `Object.entries`. The signature is intentionally permissive so callers
 * can pass named interfaces (which TypeScript does not consider compatible
 * with `Record<string, QueryValue>` due to missing index signatures) without
 * casting. Runtime stringification handles non-`QueryValue` values via
 * `String(v)`.
 */
export function qs(params?: object): string {
  if (!params) return '';
  const entries = Object.entries(params).filter(([, v]) => v !== undefined);
  if (entries.length === 0) return '';
  const sp = new URLSearchParams();
  for (const [k, v] of entries) sp.set(k, String(v));
  return `?${sp.toString()}`;
}
